import moment from 'moment';

// Hebrew Unicode block. If the first "strong" (letter) character is Hebrew, treat
// the whole string as RTL — matches how messengers decide bubble/input direction.
const HEBREW = /[֐-׿]/;

export const isRTLText = (text) => {
  if (!text) return false;
  // Find the first letter (skip spaces, digits, punctuation, emoji).
  const firstLetter = [...String(text)].find((ch) => /\p{L}/u.test(ch));
  if (!firstLetter) return false;
  return HEBREW.test(firstLetter);
};

export const dirFor = (text) => (isRTLText(text) ? 'rtl' : 'ltr');

// Soft, deterministic avatar color from a name/id — turns the initials avatars
// into a light, varied palette (instead of a wall of one dark color).
const AVATAR_PALETTE = [
  { bg: '#E6FBF1', fg: '#0c7a4d' }, // green (brand-tinted)
  { bg: '#E8F1FF', fg: '#1d5fd6' }, // blue
  { bg: '#FFF1E6', fg: '#c2620f' }, // amber
  { bg: '#F3E9FF', fg: '#7b3fd1' }, // purple
  { bg: '#FFE9F0', fg: '#c4255f' }, // pink
  { bg: '#E6FAFA', fg: '#0a8f95' }, // teal
  { bg: '#FBF0E0', fg: '#9a6b16' }, // sand
  { bg: '#EAF0F2', fg: '#3f5765' }, // slate
];

// Deleted accounts get a neutral grey rather than a palette slot. Their sender uid
// is nulled at purge, so avatarColor would fall back to the label — giving every
// deleted member the same colour AND the same "D", which reads as one person
// having written all of them.
export const DELETED_AVATAR = { bg: '#F2F4F5', fg: '#98a4ab' };

export const avatarColor = (key) => {
  const s = String(key || '');
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

export const initialsOf = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '?';
};

// Relative time for conversation rows (compact): now / 5m / 3h / Mon / 12 Jun.
export const compactTime = (unixSeconds) => {
  if (!unixSeconds) return '';
  const m = moment(unixSeconds * 1000);
  const diffMin = moment().diff(m, 'minutes');
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = moment().diff(m, 'hours');
  if (diffHr < 24) return `${diffHr}h`;
  if (moment().diff(m, 'days') < 7) return m.format('ddd');
  return m.format('D MMM');
};

// Full time inside the thread (hover / under bubble).
export const bubbleTime = (unixSeconds) =>
  unixSeconds ? moment(unixSeconds * 1000).format('HH:mm') : '';

export const dayLabel = (unixSeconds) => {
  const m = moment(unixSeconds * 1000);
  if (m.isSame(moment(), 'day')) return 'Today';
  if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
  return m.format('dddd, D MMM YYYY');
};

export const lastSeenLabel = (iso) => {
  if (!iso) return 'Offline';
  return `Last seen ${moment(iso).fromNow()}`;
};

// Online = a foreground heartbeat within the last 60s (matches backend presence window).
export const isOnlineFromLastSeen = (iso) => {
  if (!iso) return false;
  return moment().diff(moment(iso), 'seconds') <= 60;
};

// Classify a message for rendering.
export const fileKind = (message) => {
  const url = message.attachment || '';
  const name = (message.attachmentName || url || '').toLowerCase();
  if (message.type === 'image') return 'image';
  if (message.type === 'video') return 'video';
  if (message.type === 'custom') return 'custom';
  if (message.type === 'system') return 'system';
  if (message.type === 'file') {
    if (name.endsWith('.pdf')) return 'pdf';
    return 'file';
  }
  return 'text';
};

// Split text into plain/link parts for inline linkification (no extra dep).
// Returns [{ type: 'text'|'link', value }]. URLs: http(s):// or www.
const URL_RE = /((?:https?:\/\/|www\.)[^\s]+)/gi;
export const linkifyParts = (text) => {
  if (!text) return [];
  const parts = [];
  let lastIndex = 0;
  let m;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(text)) !== null) {
    if (m.index > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, m.index) });
    }
    const raw = m[0];
    const href = raw.startsWith('http') ? raw : `https://${raw}`;
    parts.push({ type: 'link', value: raw, href });
    lastIndex = m.index + raw.length;
  }
  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return parts;
};

// Preview text for the conversation list last-message line.
export const previewOf = (lastMessage) => {
  if (!lastMessage) return 'New conversation';
  switch (lastMessage.type) {
    case 'image':
      return '📷 Photo';
    case 'video':
      return '🎥 Video';
    case 'file':
      return `📎 ${lastMessage.attachmentName || 'File'}`;
    case 'custom':
      return lastMessage.text || 'Message';
    case 'system':
      return lastMessage.text || '';
    default:
      return lastMessage.text || '';
  }
};
